import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "../contexts/ThemeContext.jsx";
import { useUnsavedChanges } from "../contexts/UnsavedChangesContext.jsx";

const Header = ({
  employees,
  selectedEmployee,
  onEmployeeChange,
  apiConnected,
  currentView,
  onViewChange,
  navigationItems,
  user,
  pageTitle = "ZEITERFASSUNG",
  isFreelancer = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  // Navigation aus einem Editor mit ungespeicherten Aenderungen fragt zuerst nach.
  const { requestNavigation } = useUnsavedChanges();
  const { currentTheme, toggleTheme } = useTheme();
  const [employeeDropdownOpen, setEmployeeDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleEmployeeSelect = (employee) => {
    onEmployeeChange(employee);
    setEmployeeDropdownOpen(false);
  };

  // Navigate to URL path
  const handleNavClick = (item) => {
    requestNavigation(() => {
      if (item.path) {
        navigate(item.path);
      }
      onViewChange(item.id);
    });
  };

  const handleMobileNavClick = (item) => {
    requestNavigation(() => {
      if (item.path) {
        navigate(item.path);
      }
      onViewChange(item.id);
      setMobileMenuOpen(false);
    });
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  // Check if current path matches navigation item
  const isActive = (item) => {
    if (item.path) {
      return location.pathname.startsWith(item.path);
    }
    return currentView === item.id;
  };

  // Check if a subtab is active — pick the longest (most specific) matching path
  const isSubtabActive = (subtab) => {
    const matches = (s) => location.pathname === s.path || location.pathname.startsWith(s.path + "/");
    if (!matches(subtab)) return false;
    // Ensure no sibling subtab has a longer (more specific) match
    const siblings = subtabs.filter((s) => s.id !== subtab.id && matches(s));
    return !siblings.some((s) => s.path.length > subtab.path.length);
  };

  // Get active parent's subtabs
  const activeParent = navigationItems.find((item) => isActive(item));
  const subtabs = activeParent?.subtabs || [];

  const handleSubtabClick = (subtab) => {
    requestNavigation(() => navigate(subtab.path));
  };

  const logoSrc = `/MF_Logo_RGB_${currentTheme === "dark" ? "White" : "Black"}.png`;

  return (
    <div className="grid-header">
      {/* Logo und Avatar Section */}
      <div className="header-top">
        <div className="header-content">
          <div className="logo-section">
            <div className="logo">
              <a href="/" aria-label="Malheur&Fortuna Logo">
                <img src={logoSrc} alt="Malheur&Fortuna Logo" />
              </a>
            </div>
            
            {/* Avatar als Dropdown - nur Desktop */}
            {apiConnected && employees.length > 0 && (
              <div className="user-avatar-dropdown">
                <div
                  className="user-avatar"
                  onClick={() => setEmployeeDropdownOpen(!employeeDropdownOpen)}
                >
                  {selectedEmployee 
                    ? `${selectedEmployee.firstName.charAt(0)}${selectedEmployee.lastName.charAt(0)}`
                    : user?.name?.substring(0, 2) || "U"
                  }
                </div>
                
                {employeeDropdownOpen && (
                  <div className="dropdown-menu">
                    {employees.map((employee) => (
                      <button
                        key={employee.uuid}
                        onClick={() => handleEmployeeSelect(employee)}
                        className={`dropdown-item ${
                          selectedEmployee?.uuid === employee.uuid ? "selected" : ""
                        }`}
                      >
                        {employee.firstName} {employee.lastName}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* Avatar for freelancers - no employee dropdown needed */}
            {isFreelancer && (
              <div className="user-avatar-dropdown">
                <div className="user-avatar freelancer-avatar" title="Freelancer">
                  {user?.name?.substring(0, 2) || "FL"}
                </div>
              </div>
            )}

            {/* Avatar wenn nicht verbunden - nur Desktop (non-freelancers) */}
            {!apiConnected && !isFreelancer && (
              <div className="user-avatar-dropdown">
                <div className="user-avatar">
                  {user?.name?.substring(0, 2) || "U"}
                </div>
              </div>
            )}
          </div>

          {/* Status Indicator rechts */}
          <div className="status-info">
            <span className="status-text">
              {isFreelancer ? "Freelancer" : apiConnected ? "Verbunden" : "Offline"}
            </span>
            <div className={`status-indicator ${isFreelancer ? "freelancer" : apiConnected ? "connected" : "disconnected"}`}></div>
          </div>
          
          {/* Hamburger Button - nur Mobile */}
          <button 
            className="hamburger-button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <div className={`hamburger-icon ${mobileMenuOpen ? 'open' : ''}`}>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </button>
        </div>
      </div>

      {/* Page Title und Navigation Section - nur Desktop */}
      <div className="page-title-section">
        <h1>{pageTitle}</h1>
        
        {apiConnected && navigationItems && (
          <nav className="navigation">
            {navigationItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNavClick(item)}
                className={`nav-item ${isActive(item) ? "active" : ""} ${
                  item.requiresEmployee && !selectedEmployee ? "disabled" : ""
                }`}
                disabled={item.requiresEmployee && !selectedEmployee}
              >
                {item.label}
              </button>
            ))}
            
            {/* Theme Slider */}
            <div className="theme-slider">
              <div
                className={`slider-option ${currentTheme === 'light' ? 'active' : ''}`}
                onClick={() => currentTheme !== 'light' && toggleTheme()}
              >
                L
              </div>
              <div
                className={`slider-option ${currentTheme === 'dark' ? 'active' : ''}`}
                onClick={() => currentTheme !== 'dark' && toggleTheme()}
              >
                D
              </div>
            </div>

            {/* Dashboard Button */}
            <button
              className={`nav-dashboard-btn${location.pathname === '/main-dashboard' ? ' active' : ''}`}
              onClick={() => requestNavigation(() => navigate('/main-dashboard'))}
              title="Dashboard"
            >
              DB
            </button>
          </nav>
        )}

        {/* Subtabs row */}
        {apiConnected && subtabs.length > 0 && (
          <nav className="sub-navigation">
            {subtabs.map((subtab) => (
              <button
                key={subtab.id}
                onClick={() => handleSubtabClick(subtab)}
                className={`sub-nav-item ${isSubtabActive(subtab) ? "active" : ""}`}
              >
                {subtab.label}
              </button>
            ))}
          </nav>
        )}
      </div>

      {/* Mobile Menu */}
      <div className="mobile-menu">
        {/* Overlay */}
        <div 
          className={`mobile-overlay ${mobileMenuOpen ? 'open' : ''}`}
          onClick={closeMobileMenu}
        />
        
        {/* Menu Panel */}
        <div className={`mobile-menu-panel ${mobileMenuOpen ? 'open' : ''}`}>
          <div className="mobile-menu-header">
            <h3>Menu</h3>
            <button className="close-button" onClick={closeMobileMenu}>
              ×
            </button>
          </div>
          
          <div className="mobile-menu-content">
            {/* Navigation */}
            <div className="mobile-nav-section">
              <h4>Navigation</h4>
              {navigationItems.map((item) => (
                <React.Fragment key={item.id}>
                  <button
                    onClick={() => handleMobileNavClick(item)}
                    className={`mobile-nav-item ${isActive(item) ? "active" : ""} ${
                      item.requiresEmployee && !selectedEmployee ? "disabled" : ""
                    }`}
                    disabled={item.requiresEmployee && !selectedEmployee}
                  >
                    {item.label}
                  </button>
                  {item.subtabs && isActive(item) && (
                    <div className="mobile-subtabs">
                      {item.subtabs.map((subtab) => (
                        <button
                          key={subtab.id}
                          onClick={() =>
                            requestNavigation(() => {
                              navigate(subtab.path);
                              setMobileMenuOpen(false);
                            })
                          }
                          className={`mobile-sub-nav-item ${isSubtabActive(subtab) ? "active" : ""}`}
                        >
                          {subtab.label}
                        </button>
                      ))}
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
            
            {/* Theme Switcher */}
            <div className="mobile-nav-section mobile-theme-section">
              <h4>Darstellung</h4>
              <div className="mobile-theme-slider">
                <div 
                  className={`mobile-slider-option ${currentTheme === 'light' ? 'active' : ''}`}
                  onClick={() => currentTheme !== 'light' && toggleTheme()}
                >
                  L
                </div>
                <div 
                  className={`mobile-slider-option ${currentTheme === 'dark' ? 'active' : ''}`}
                  onClick={() => currentTheme !== 'dark' && toggleTheme()}
                >
                  D
                </div>
              </div>
            </div>
            
            {/* User Info & Employee Selector */}
            <div className="mobile-user-section">
              <div className="mobile-user-info">
                <div className="mobile-user-avatar">
                  {selectedEmployee 
                    ? `${selectedEmployee.firstName.charAt(0)}${selectedEmployee.lastName.charAt(0)}`
                    : user?.name?.substring(0, 2) || "U"
                  }
                </div>
                <div className="mobile-user-details">
                  <div className="mobile-user-name">
                    {selectedEmployee
                      ? `${selectedEmployee.firstName} ${selectedEmployee.lastName}`
                      : user?.name || "Benutzer"}
                  </div>
                  <div className="mobile-status">
                    <div className={`mobile-status-dot ${apiConnected ? "connected" : "disconnected"}`}></div>
                    <span>{apiConnected ? "Verbunden" : "Offline"}</span>
                  </div>
                </div>
              </div>
              
              {/* Employee Selection */}
              {apiConnected && employees.length > 0 && (
                <div className="mobile-nav-section">
                  <h4>TEAM</h4>
                  {employees.map((employee) => (
                    <button
                      key={employee.uuid}
                      onClick={() => {
                        handleEmployeeSelect(employee);
                        closeMobileMenu();
                      }}
                      className={`mobile-nav-item ${
                        selectedEmployee?.uuid === employee.uuid ? "active" : ""
                      }`}
                    >
                      {employee.firstName} {employee.lastName}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;